/**
 * Componente Input - Sistema de Diseño Illustra
 * Input reutilizable con label, error y helper text
 */

const Input = ({ 
  label,
  error,
  helperText,
  type = 'text',
  placeholder,
  value,
  onChange,
  onBlur,
  onFocus,
  name,
  id,
  required = false,
  disabled = false,
  readOnly = false,
  autoComplete,
  maxLength,
  min,
  max,
  step,
  icon,
  iconPosition = 'left',
  className = '',
  inputClassName = '',
  ...props 
}) => {
  const inputId = id || name || `input-${Math.random().toString(36).substr(2, 9)}`;
  
  const baseInputClasses = `
    block w-full px-4 py-2.5 rounded-lg
    bg-white dark:bg-slate-800
    border-2 
    text-gray-900 dark:text-gray-100
    placeholder-gray-400 dark:placeholder-gray-500
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-0
    disabled:opacity-50 disabled:cursor-not-allowed
    read-only:bg-gray-50 dark:read-only:bg-slate-900
  `.trim().replace(/\s+/g, ' ');
  
  const borderClasses = error 
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' 
    : 'border-gray-300 dark:border-slate-600 focus:border-indigo-500 focus:ring-indigo-500/20';
  
  const withIconClasses = icon ? (iconPosition === 'left' ? 'pl-10' : 'pr-10') : '';
  
  return (
    <div className={`space-y-1.5 ${className}`}>
      {/* Label */}
      {label && (
        <label 
          htmlFor={inputId} 
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {/* Input Container */}
      <div className="relative">
        {/* Icon */}
        {icon && (
          <div 
            className={`
              absolute top-1/2 -translate-y-1/2
              ${iconPosition === 'left' ? 'left-3' : 'right-3'}
              text-gray-400 dark:text-gray-500
              pointer-events-none
            `}
          >
            {icon}
          </div>
        )}
        
        {/* Input */}
        <input
          type={type}
          id={inputId}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          autoComplete={autoComplete}
          maxLength={maxLength}
          min={min}
          max={max}
          step={step}
          className={`
            ${baseInputClasses} 
            ${borderClasses} 
            ${withIconClasses}
            ${inputClassName}
          `}
          {...props}
        />
      </div>
      
      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 flex items-start gap-1">
          <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </p>
      )}
      
      {/* Helper Text */}
      {helperText && !error && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {helperText}
        </p>
      )}
    </div>
  );
};

export default Input;
